import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns/format";
import { useMemo, useState } from "react";
import { CopyButton, EllipsisText, ReplyStatusBadge } from "@/components/common";
import { AutoPagination, DataTable, useServerTable } from "@/components/table";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  SearchBar,
  Tabs,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import useDeliveryDateHistory from "@/features/shared/hooks/use-delivery-date-history";
import useRequirementChangeHistory from "@/features/shared/hooks/use-requirement-change-history";
import useShipmentRequestHistory from "@/features/shared/hooks/use-shipment-request-history";
import type {
  AllHistoriesData,
  DeliveryStatus,
  HistoryTabType,
  RequirementStatus,
  ShipmentStatus,
} from "@/types/shared.type";
import { formatNumberWithCommas } from "@/utils/format-number";

export const Route = createFileRoute("/_protected/supplier/history")({
  component: RouteComponent,
});

const deliveryDateColumns: ColumnDef<AllHistoriesData>[] = [
  {
    accessorKey: "recordId",
    header: "ID",
    cell: ({ getValue }) => {
      const recordId = getValue<string>();
      return <CopyButton text={recordId} />;
    },
  },
  {
    accessorKey: "poNo",
    header: "採購單號",
  },
  {
    accessorKey: "itemNo",
    header: "項次",
  },
  {
    accessorKey: "productNo",
    header: "料號",
  },
  {
    accessorKey: "productName",
    header: "品名",
    cell: ({ getValue }) => {
      const productName = getValue<string>();
      return <EllipsisText maxWidth={250}>{productName}</EllipsisText>;
    },
  },
  {
    accessorKey: "centralFactoryName",
    header: "客戶",
  },
  {
    accessorKey: "requiredDate",
    header: "需求交期",
    cell: ({ getValue }) => {
      const requiredDate = getValue<string>();
      return <Typography variant="mono">{requiredDate}</Typography>;
    },
  },
  {
    accessorKey: "deliveryDate",
    header: "回覆交期",
    cell: ({ getValue }) => {
      const deliveryDate = getValue<string>();
      return <Typography variant="mono">{deliveryDate}</Typography>;
    },
  },
  {
    accessorKey: "commitQty",
    header: "回覆數量",
    cell: ({ getValue }) => {
      const commitQty = getValue<string>();
      return <Typography variant="mono">{formatNumberWithCommas(commitQty)}</Typography>;
    },
  },
  {
    accessorKey: "deliveryStatus",
    header: "狀態",
    cell: ({ getValue }) => {
      const status = getValue<DeliveryStatus>();
      return <ReplyStatusBadge status={status} />;
    },
  },
  {
    accessorKey: "deliveryNote",
    header: "備註",
    cell: ({ getValue }) => {
      const deliveryNote = getValue<string>();
      if (!deliveryNote) return null;
      return <EllipsisText maxWidth={250}>{deliveryNote}</EllipsisText>;
    },
  },
  {
    accessorKey: "userNickName",
    header: "回報使用者",
  },
  {
    accessorKey: "createdAt",
    header: "創建時間",
    cell: ({ getValue }) => {
      const createdAt = getValue<string>();
      return (
        <Typography variant="mono">{format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss")}</Typography>
      );
    },
  },
];

const requirementChangeColumns: ColumnDef<AllHistoriesData>[] = [
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ getValue }) => {
      const id = getValue<string>();
      return <CopyButton text={id} />;
    },
  },
  {
    accessorKey: "poNo",
    header: "採購單號",
  },
  {
    accessorKey: "itemNo",
    header: "項次",
  },
  {
    accessorKey: "productNo",
    header: "料號",
  },
  {
    accessorKey: "productName",
    header: "品名",
    cell: ({ getValue }) => {
      const productName = getValue<string>();
      return <EllipsisText maxWidth={250}>{productName}</EllipsisText>;
    },
  },
  {
    accessorKey: "centralFactoryName",
    header: "客戶",
  },
  {
    accessorKey: "requiredDate",
    header: "需求交期",
    cell: ({ getValue }) => {
      const requiredDate = getValue<string>();
      return <Typography variant="mono">{requiredDate}</Typography>;
    },
  },
  {
    accessorKey: "orderQty",
    header: "需求數量",
    cell: ({ getValue }) => {
      const orderQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(orderQty)}</Typography>;
    },
  },
  {
    accessorKey: "status",
    header: "狀態",
    cell: ({ getValue }) => {
      const status = getValue<RequirementStatus>();

      const variants = {
        auto_sync: "success",
        modified: "warning",
        approved_modified: "warning",
      } as const;

      const labels = {
        auto_sync: "新增",
        modified: "變更",
        approved_modified: "調整",
      } as const;

      return (
        <Badge variant={variants[status as keyof typeof variants]}>
          {labels[status as keyof typeof labels]}
        </Badge>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: "創建時間",
    cell: ({ getValue }) => {
      const createdAt = getValue<string>();
      return (
        <Typography variant="mono">{format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss")}</Typography>
      );
    },
  },
];

const shipmentRequestColumns: ColumnDef<AllHistoriesData>[] = [
  {
    accessorKey: "requestId",
    header: "ID",
    cell: ({ getValue }) => {
      const requestId = getValue<string>();
      return <CopyButton text={requestId} />;
    },
  },
  {
    accessorKey: "poNo",
    header: "採購單號",
  },
  {
    accessorKey: "itemNo",
    header: "項次",
  },
  {
    accessorKey: "productNo",
    header: "料號",
  },
  {
    accessorKey: "productName",
    header: "品名",
    cell: ({ getValue }) => {
      const productName = getValue<string>();
      return <EllipsisText maxWidth={250}>{productName}</EllipsisText>;
    },
  },
  {
    accessorKey: "centralFactoryName",
    header: "客戶",
  },
  {
    accessorKey: "shipDate",
    header: "交貨日期",
    cell: ({ getValue }) => {
      const shipDate = getValue<string | null>();
      // 防止切換 tab 時出現錯誤
      if (!shipDate) return null;
      return <Typography variant="mono">{format(new Date(shipDate), "yyyy-MM-dd")}</Typography>;
    },
  },
  {
    accessorKey: "qty",
    header: "交貨數量",
    cell: ({ getValue }) => {
      const qty = getValue<string>();
      return <Typography variant="mono">{formatNumberWithCommas(qty)}</Typography>;
    },
  },
  {
    accessorKey: "status",
    header: "狀態",
    cell: ({ getValue }) => {
      const status = getValue<ShipmentStatus>();

      const variants = {
        applied: "info",
        prohibited: "destructive",
        restored: "success",
      } as const;

      const labels = {
        applied: "申請",
        prohibited: "禁止",
        restored: "恢復",
      } as const;

      return (
        <Badge variant={variants[status as keyof typeof variants]}>
          {labels[status as keyof typeof labels]}
        </Badge>
      );
    },
  },
  {
    accessorKey: "note",
    header: "備註",
  },
  {
    accessorKey: "userNickName",
    header: "回報使用者",
  },
  {
    accessorKey: "createdAt",
    header: "創建時間",
    cell: ({ getValue }) => {
      const createdAt = getValue<string>();
      return (
        <Typography variant="mono">{format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss")}</Typography>
      );
    },
  },
];

function RouteComponent() {
  const [tab, setTab] = useState<HistoryTabType>("requirement-change");

  const isMobile = useIsMobile();

  const uiConfig = useAuthStore.use.uiConfig();
  const useShipmentRequestFunction = uiConfig.useShipmentRequestFunction;

  const { queryFn, columns } = useMemo(() => {
    switch (tab) {
      case "delivery-date":
        return {
          queryFn: useDeliveryDateHistory,
          columns: deliveryDateColumns,
        };
      case "requirement-change":
        return {
          queryFn: useRequirementChangeHistory,
          columns: requirementChangeColumns,
        };
      case "shipment-request":
        return {
          queryFn: useShipmentRequestHistory,
          columns: shipmentRequestColumns,
        };
    }
  }, [tab]);

  const headerBgColor = useMemo(() => {
    switch (tab) {
      case "delivery-date":
        return "bg-green-50";
      case "requirement-change":
        return "bg-red-50";
      case "shipment-request":
        return "bg-blue-50";
    }
  }, [tab]);

  const {
    table,
    isLoading,
    isFetching,
    globalFilter,
    setGlobalFilter,
    onPaginationChange,
    resetPageIndexAndFilter,
  } = useServerTable<AllHistoriesData>({
    queryFn,
    columns,
    initialPageSize: 20,
  });

  return (
    <section>
      <title>供應商管理頁面｜Supplier Links</title>

      <Card>
        <CardHeader>
          <Typography variant="h2">歷程紀錄</Typography>
        </CardHeader>

        <CardContent className="flex flex-col space-y-4">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Tabs
                value={tab}
                onValueChange={(value) => {
                  setTab(value as typeof tab);
                  resetPageIndexAndFilter();
                }}
                className="overflow-x-auto"
              >
                <TabsList className="min-w-max flex-nowrap">
                  <TabsTrigger value="requirement-change">需求變更歷程</TabsTrigger>
                  <TabsTrigger value="delivery-date">回覆交期歷程</TabsTrigger>
                  {useShipmentRequestFunction && (
                    <TabsTrigger value="shipment-request">交貨歷程</TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>

            <div className="flex items-center gap-2">
              <span className="w-full md:w-auto">
                <SearchBar
                  className="grow"
                  placeholder="請輸入採購單號、客戶、料號或品名..."
                  value={globalFilter}
                  onChange={setGlobalFilter}
                />
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <Typography variant="muted">資料載入中...</Typography>
            </div>
          ) : (
            <>
              <DataTable table={table} headerBgClass={headerBgColor} isFetching={isFetching} />

              <AutoPagination<AllHistoriesData>
                table={table}
                showTotalCount
                showSizeChanger={!isMobile}
                onPaginationChange={onPaginationChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
